import axiosInstance from '../../../../lib/axios';
import type {
  AdminAgentAvatarUploadResponse,
  AdminAgentDetail,
  AdminAgentFileToolPermission,
  AdminAgentFileToolPermissionsResponse,
  AdminAgentStats,
  CreateSystemAgentInput,
  ListAdminAgentsParams,
  ListAdminAgentsResponse,
  UpdateAdminAgentFileToolPermissionInput,
  UpdateAdminAgentInput,
  UpdateAdminAgentStatusInput,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

const BASE_URL = '/v1/admin/agents';

export const getAdminAgentStats = async (): Promise<AdminAgentStats> => {
  const response = await axiosInstance.get<ApiResponse<AdminAgentStats>>(`${BASE_URL}/stats`);
  return response.data.data;
};

export const getAdminAgents = async (
  params?: ListAdminAgentsParams
): Promise<ListAdminAgentsResponse> => {
  const response = await axiosInstance.get<ApiResponse<ListAdminAgentsResponse>>(BASE_URL, {
    params: {
      search: params?.search || undefined,
      ownerType: params?.ownerType || undefined,
      status: params?.status || undefined,
      active: params?.active && params.active !== 'all' ? params.active : undefined,
      page: params?.page || 1,
      limit: params?.limit || 20,
    },
  });
  return response.data.data;
};

export const getAdminAgent = async (publicId: string): Promise<AdminAgentDetail> => {
  const response = await axiosInstance.get<ApiResponse<AdminAgentDetail>>(
    `${BASE_URL}/${publicId}`
  );
  return response.data.data;
};

export const createSystemAgent = async (
  data: CreateSystemAgentInput
): Promise<AdminAgentDetail> => {
  const response = await axiosInstance.post<ApiResponse<AdminAgentDetail>>(BASE_URL, data);
  return response.data.data;
};

export const updateAdminAgent = async (
  publicId: string,
  data: UpdateAdminAgentInput
): Promise<AdminAgentDetail> => {
  const response = await axiosInstance.patch<ApiResponse<AdminAgentDetail>>(
    `${BASE_URL}/${publicId}`,
    data
  );
  return response.data.data;
};

export const updateAdminAgentStatus = async (
  publicId: string,
  data: UpdateAdminAgentStatusInput
): Promise<AdminAgentDetail> => {
  const response = await axiosInstance.patch<ApiResponse<AdminAgentDetail>>(
    `${BASE_URL}/${publicId}/status`,
    data
  );
  return response.data.data;
};

export const publishAdminAgent = async (publicId: string): Promise<AdminAgentDetail> => {
  const response = await axiosInstance.post<ApiResponse<AdminAgentDetail>>(
    `${BASE_URL}/${publicId}/publish`
  );
  return response.data.data;
};

export const uploadAdminAgentAvatar = async (
  file: File
): Promise<AdminAgentAvatarUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post<ApiResponse<AdminAgentAvatarUploadResponse>>(
    `${BASE_URL}/avatar`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.data;
};

export const getAdminAgentFileTools = async (
  publicId: string
): Promise<AdminAgentFileToolPermissionsResponse> => {
  const response = await axiosInstance.get<ApiResponse<AdminAgentFileToolPermissionsResponse>>(
    `${BASE_URL}/${publicId}/file-tools`
  );
  return response.data.data;
};

export const updateAdminAgentFileTool = async (
  publicId: string,
  toolId: string,
  data: UpdateAdminAgentFileToolPermissionInput
): Promise<AdminAgentFileToolPermission> => {
  const response = await axiosInstance.put<ApiResponse<AdminAgentFileToolPermission>>(
    `${BASE_URL}/${publicId}/file-tools/${toolId}`,
    data
  );
  return response.data.data;
};
